import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Shirt, Users, CalendarDays, FlaskConical } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription
} from '../../components/ui/dialog';
import { getCloudinaryThumbnail } from '../../lib/cloudinaryUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = '#C9A962';

const chartTooltipStyle = {
  contentStyle: {
    backgroundColor: '#1a1a1a',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
  },
  labelStyle: { color: 'rgba(255,255,255,0.6)' },
};

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

function formatDateTime(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AdminTryOnPage() {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySkip, setHistorySkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);

  const token = localStorage.getItem('davocado-admin-token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, historyRes] = await Promise.all([
          axios.get(`${API}/tryon/stats`, { headers }),
          axios.get(`${API}/tryon/history?skip=0&limit=20`, { headers }),
        ]);
        setStats(statsRes.data);
        setHistory(historyRes.data);
        setHasMore(historyRes.data.length === 20);
      } catch (error) {
        console.error('Error fetching try-on data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const loadMore = async () => {
    const nextSkip = historySkip + 20;
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/tryon/history?skip=${nextSkip}&limit=20`, { headers });
      setHistory((prev) => [...prev, ...res.data]);
      setHistorySkip(nextSkip);
      setHasMore(res.data.length === 20);
    } catch (error) {
      console.error('Error loading more history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 skeleton rounded-none" />
          ))}
        </div>
        <div className="h-80 skeleton rounded-none" />
      </div>
    );
  }

  const isEmpty = !stats || stats.total_sessions === 0;

  if (isEmpty) {
    return (
      <div className="space-y-8" data-testid="admin-tryon">
        <div>
          <h1 className="text-2xl font-serif text-white mb-2">Probador Virtual</h1>
          <p className="text-white/60">Estadísticas e historial del probador virtual</p>
        </div>
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-12">
            <div className="text-center">
              <Shirt className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/40 text-lg">No hay datos del probador aún</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Pruebas Hoy',
      value: stats?.today_sessions || 0,
      icon: FlaskConical,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      title: 'Esta Semana',
      value: stats?.week_sessions || 0,
      icon: CalendarDays,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'Total Pruebas',
      value: stats?.total_sessions || 0,
      icon: Shirt,
      color: 'text-[#C9A962]',
      bgColor: 'bg-[#C9A962]/10',
    },
    {
      title: 'Usuarios Únicos',
      value: stats?.unique_users || 0,
      subtitle: `${stats?.today_unique_users || 0} hoy`,
      icon: Users,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
  ];

  return (
    <div className="space-y-8" data-testid="admin-tryon">
      <div>
        <h1 className="text-2xl font-serif text-white mb-2">Probador Virtual</h1>
        <p className="text-white/60">Estadísticas e historial del probador virtual</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{stat.title}</p>
                    <p className="text-2xl font-semibold text-white">
                      {stat.value.toLocaleString()}
                    </p>
                    {stat.subtitle && (
                      <p className="text-white/40 text-sm mt-1">{stat.subtitle}</p>
                    )}
                  </div>
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-serif">Uso Diario</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.daily_usage?.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.daily_usage}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis
                      dataKey="date"
                      stroke="rgba(255,255,255,0.3)"
                      tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                      tickFormatter={formatDate}
                    />
                    <YAxis
                      stroke="rgba(255,255,255,0.3)"
                      tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                    />
                    <Tooltip
                      {...chartTooltipStyle}
                      formatter={(value) => [`${value} pruebas`, 'Sesiones']}
                      labelFormatter={(label) => formatDate(label)}
                    />
                    <Bar dataKey="sessions" fill={GOLD} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-white/40 text-center py-8">No hay datos de uso diario</p>
            )}
          </CardContent>
        </Card>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-serif">Productos Más Probados</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.most_tried_products?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="py-3 px-4 text-left text-white/60 font-medium w-12">#</th>
                        <th className="py-3 px-4 text-left text-white/60 font-medium">Producto</th>
                        <th className="py-3 px-4 text-right text-white/60 font-medium">Pruebas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.most_tried_products.map((product, index) => (
                        <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="py-3 px-4">
                            <span className="w-7 h-7 rounded-lg bg-[#C9A962]/10 flex items-center justify-center text-[#C9A962] text-xs font-medium">
                              {index + 1}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-white font-medium">{product.product_name}</td>
                          <td className="py-3 px-4 text-right text-white">{product.count.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-white/40 text-center py-8">No hay datos de productos</p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-serif">Historial Reciente</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length > 0 ? (
                <div className="space-y-3">
                  {history.map((session, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0"
                    >
                      {session.user_photo_url && (
                        <img
                          src={getCloudinaryThumbnail(session.user_photo_url)}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                        />
                      )}
                      {session.result_image_url && (
                        <button
                          onClick={() => setPreviewImage(session.result_image_url)}
                          className="flex-shrink-0"
                        >
                          <img
                            src={getCloudinaryThumbnail(session.result_image_url)}
                            alt="Resultado"
                            className="w-10 h-10 rounded object-cover hover:opacity-80 transition-opacity cursor-pointer"
                          />
                        </button>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {session.product_name}
                        </p>
                        <p className="text-white/40 text-xs">{formatDateTime(session.created_at)}</p>
                      </div>
                    </div>
                  ))}

                  {hasMore && (
                    <button
                      onClick={loadMore}
                      disabled={historyLoading}
                      className="w-full py-2 text-sm text-[#C9A962] hover:text-[#C9A962]/80 transition-colors disabled:opacity-50"
                    >
                      {historyLoading ? 'Cargando...' : 'Ver más'}
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-white/40 text-center py-8">No hay historial aún</p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 sm:max-w-2xl p-2 sm:p-4">
          <DialogTitle className="sr-only">Vista previa del resultado</DialogTitle>
          <DialogDescription className="sr-only">Imagen del resultado del probador virtual</DialogDescription>
          {previewImage && (
            <img
              src={previewImage}
              alt="Resultado del probador virtual"
              className="w-full h-auto rounded"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
